import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MoreVertical, LucideIcon } from "lucide-react";
import { ReactNode } from "react";

export interface ActionItem {
  label: ReactNode;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /**
   * Optional tooltip shown on hover/focus. Especially useful when the item is
   * `disabled` so users understand *why* the action is unavailable.
   */
  tooltip?: ReactNode;
}

export interface ActionGroup {
  label?: string;
  items: ActionItem[];
}

interface ActionsDropdownProps {
  actions: (ActionItem | ActionGroup)[];
  align?: "start" | "center" | "end";
  className?: string;
}

export const ActionsDropdown = ({
  actions,
  align = "end",
  className
}: ActionsDropdownProps) => {
  const renderActionItem = (action: ActionItem, key: string) => {
    const item = (
      <DropdownMenuItem
        key={key}
        onClick={action.onClick}
        disabled={action.disabled}
        className={action.destructive ? "text-destructive focus:text-destructive" : ""}
        // When the item is disabled, capture pointer events so the wrapping
        // tooltip trigger can still detect hover/focus.
        onSelect={action.disabled ? (e) => e.preventDefault() : undefined}
      >
        {action.icon && <action.icon className="mr-2 h-4 w-4" />}
        <span className="flex-1">{action.label}</span>
      </DropdownMenuItem>
    );

    if (!action.tooltip) return item;

    return (
      <Tooltip key={key}>
        {/*
          asChild + a span wrapper lets the tooltip work even when the inner
          DropdownMenuItem is disabled (disabled elements don't fire pointer
          events). The span is inert visually and forwards focus.
        */}
        <TooltipTrigger asChild>
          <span className={action.disabled ? "block cursor-not-allowed" : "block"}>
            {item}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">{action.tooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const renderContent = () => {
    return actions.map((item, index) => {
      if ('items' in item) {
        // It's a group
        const group = item as ActionGroup;
        return (
          <div key={index}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.label && <DropdownMenuLabel>{group.label}</DropdownMenuLabel>}
            {group.items.map((action, actionIndex) =>
              renderActionItem(action, `${index}-${actionIndex}`)
            )}
          </div>
        );
      } else {
        // It's a single action
        const action = item as ActionItem;
        return renderActionItem(action, index.toString());
      }
    });
  };

  return (
    <div className="flex justify-end">
      <TooltipProvider delayDuration={150}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className={`h-8 w-8 ${className || ""}`} aria-label="Actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={align}>
            {renderContent()}
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>
    </div>
  );
};
